import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {Menu, MenuItem, MenuDivider, Position, Icon} from "@blueprintjs/core";
import {Popover2} from '@blueprintjs/popover2';
import {IconNames} from '@blueprintjs/icons';

import Avatar from '../../image/Avatar';
import {WorkspaceMenuItemElements} from './WorkspaceMenu';
import BillingOrUpgrade from './BillingOrUpgrade';
import {setIsDarkMode} from '../../../store/actions/uiActions';
import {hideIntercomChat, showIntercomDefaultLauncher} from '../../../utils/intercom';
import {setHideIntercom} from '../../../store/actions/uiActions';
import {switchWorkspace} from '../../../store/actions/workspaceActions';
import {IState} from '../../../store/reducers';
import config from '../../../../config';
import {useCurrentUser} from '../../../hooks';
import {isPremise} from '../../../utils/premise';
import {IUser} from '../../../models';

interface ContentMenuProps {
  currentUser: any;
  workspaces: any;
  onChangeWorkspace: (value: any) => void;
  hideIntercom: boolean;
  toggleIntercom: () => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const ContentMenu = ({
  currentUser,
  workspaces,
  onChangeWorkspace,
  hideIntercom,
  toggleIntercom,
  isDarkMode,
  toggleDarkMode,
}: ContentMenuProps) => {
  if (!currentUser) {
    return null;
  }

  return (
    <Menu>
        <li>
          <Link to={`/a/profile/${currentUser?.uid}`}
                className="bp4-menu-item bp4-popover-dismiss header-avatar__user-details">
            <div>
              <Icon icon={IconNames.USER}/>
            </div>
            <div className="bp4-text-overflow-ellipsis bp4-fill">
              <strong>{currentUser?.first_name + " " + currentUser?.last_name}</strong>
              <br/>
              <span className='bp4-text-small bp4-text-muted'>{currentUser?.email}</span>
            </div>
          </Link>
        </li>
        <MenuDivider/>
        <MenuItem
          icon={IconNames.APPLICATION}
          text="Switch Workspace"
          popoverProps={{
            portalClassName: 'switch-workspace-menu-item-portal'
          }}
        >
          <WorkspaceMenuItemElements
            workspaces={workspaces}
            showAllWorkspaces={true}
            onChangeWorkspace={onChangeWorkspace}
          />
        </MenuItem>
        <MenuDivider/>
        {
          !isPremise() && (
            <>
              <MenuItem text="Help & Support" icon={IconNames.LIFESAVER}>
              <MenuItem
                icon={IconNames.MANUAL}
                text="Documentation"
                href={config.app.url + '/docs'}
                target="_blank"
              />
              <MenuItem
                icon={IconNames.INFO_SIGN}
                text="Terms & Policies"
                href={config.app.url + '/legal'}
                target="_blank"
              />
              <MenuDivider/>
              <MenuItem
                icon={IconNames.CHAT}
                text={hideIntercom ? "Show Support Chat" : "Hide Support Chat"}
                onClick={toggleIntercom}
              />
              <MenuDivider/>
              <MenuItem
                className="header__version"
                text={config.app.version}
                disabled={true}
              />
            </MenuItem>
              <BillingOrUpgrade/>
              <MenuDivider/>
              <MenuItem
                text={isDarkMode ? "Light Mode" : "Dark Mode"}
                icon={isDarkMode ? IconNames.FLASH : IconNames.MOON}
                onClick={toggleDarkMode}
              />
              <MenuDivider/>
            </>
          )
        }
        <MenuItem
          text="Logout"
          intent='danger'
          icon={IconNames.LOG_OUT}
          href={config.app.url + '/logout'}
        />
      </Menu>
  )
};

const _getButtonText = (currentUser?: IUser | null): string => {
  let text = currentUser?.current_organization_name || '';

  if (currentUser?.current_workspace_name !== 'default') {
    text += ` (${currentUser?.current_workspace_name || '_'})`
  }
  return text;
}

const SettingsMenu = () => {
  const currentUser = useCurrentUser();
  const isDarkMode = useSelector((state: IState) => state.ui.isDarkMode);
  const hideIntercom = useSelector((state: IState) => state.ui.hideIntercom);
  const workspaces = useSelector((state: IState) => state.workspaces?.workspaces);
  const dispatch = useDispatch();

  useEffect(() => {
    if (hideIntercom) {
      hideIntercomChat(currentUser);
    } else {
      showIntercomDefaultLauncher(currentUser);
    }
  }, [hideIntercom]);

  const toggleDarkMode = () => {
    dispatch(setIsDarkMode(!isDarkMode));
  };

  const toggleHideIntercom = () => {
    dispatch(setHideIntercom(!hideIntercom));
  }

  const onChangeWorkspace = (workspaceUid: string) => {
    dispatch(switchWorkspace(workspaceUid, {toaster: true}));
  }

  return (
    <Popover2
      position={Position.BOTTOM}
      content={
        <ContentMenu
          currentUser={currentUser}
          workspaces={workspaces}
          onChangeWorkspace={onChangeWorkspace}
          hideIntercom={hideIntercom}
          toggleIntercom={toggleHideIntercom}
          isDarkMode={isDarkMode}
          toggleDarkMode={toggleDarkMode}
        />
      }
    >
      <Avatar
        headerAvatar={true}
        rounded={true}
        inline={true}
        names={[currentUser?.first_name || '', currentUser?.last_name || '']}
        image={currentUser?.avatar}
        buttonText={_getButtonText(currentUser)}/>
    </Popover2>
  )
};

export default SettingsMenu;
